import { useAtomValue, useSetAtom } from "jotai";
import { loadable } from "jotai/utils";
import { useCallback, useRef } from "react";

import type { ClientStoreV2 } from "@moment/reboot-differential";

import {
	$addAttachmentFromPath,
	$attachmentsBusy,
	$attachmentsList,
	$removeAttachment,
	type AttachmentInfo,
} from "../../internal/attachments";
import { $documentStoreV2 } from "../../internal/reboot/document-store-v2.atom";

export const useAttachments = (documentId: string | undefined) => {
	const listLoadable = useAtomValue(loadable($attachmentsList(documentId ?? "")));
	const prevAttachmentsRef = useRef<AttachmentInfo[]>([]);
	if (listLoadable.state === "hasData") {
		prevAttachmentsRef.current = listLoadable.data;
	}
	const attachments =
		listLoadable.state === "hasData" ? listLoadable.data : prevAttachmentsRef.current;
	const refresh = useSetAtom($attachmentsList(documentId ?? ""));
	const removeAtom = useSetAtom($removeAttachment);
	const addFromPathAtom = useSetAtom($addAttachmentFromPath);
	const isBusy = useAtomValue($attachmentsBusy(documentId ?? ""));

	const storeV2Loadable = useAtomValue(loadable($documentStoreV2(documentId ?? "")));
	const storeV2: ClientStoreV2 | undefined =
		storeV2Loadable.state === "hasData" ? (storeV2Loadable.data ?? undefined) : undefined;

	const removeAttachment = useCallback(
		async (filename: string) => {
			if (!documentId) {
				return;
			}
			await removeAtom({ documentId, filename, store: storeV2 });
		},
		[documentId, removeAtom, storeV2]
	);

	const addFromPath = useCallback(
		async (sourcePath: string) => {
			if (!documentId) {
				return;
			}
			await addFromPathAtom({ documentId, sourcePath, store: storeV2 });
		},
		[documentId, addFromPathAtom, storeV2]
	);

	return {
		attachments,
		isLoading: listLoadable.state === "loading",
		isBusy,
		removeAttachment,
		addFromPath,
		refresh,
	};
};
